import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';
import PaginatedList from '@theme/paginated-list';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * A custom element that renders a pagniated results list
 */
export default class ResultsList extends PaginatedList {
  /** @type {number} */
  #reorderToken = 0;

  /** @type {boolean} */
  #suppressUpdate = false;

  connectedCallback() {
    if (this.#isPriceSort()) this.dataset.reorderPrices = 'true';

    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.setAttribute('initialized', '');

    if (this.#isPriceSort()) this.#schedulePriceReorder();
  }

  updatedCallback() {
    super.updatedCallback();
    if (this.#suppressUpdate) return;

    if (this.#isPriceSort()) {
      this.#schedulePriceReorder();
      return;
    }

    if (this.dataset.reorderPrices === 'true') {
      delete this.dataset.reorderPrices;
      this.pages.clear();
      this.resumePaging();
    }
  }

  /**
   * Shopify sorts price-ascending by the cheapest variant. Cards show the
   * first available variant, which for sets is the full-set price.
   * @returns {boolean}
   */
  #isPriceSort() {
    const sortBy = new URL(window.location.href).searchParams.get('sort_by');
    return sortBy === 'price-ascending' || sortBy === 'price-descending';
  }

  #schedulePriceReorder() {
    const { grid } = this.refs;
    if (!grid) return;

    const sortBy = new URL(window.location.href).searchParams.get('sort_by');
    if (sortBy !== 'price-ascending' && sortBy !== 'price-descending') return;

    this.dataset.reorderPrices = 'true';
    this.dataset.priceSortPending = 'true';
    this.dataset.shopifyLastPage = grid.getAttribute('last-page') || '1';

    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }

    this.pages.clear();

    const token = ++this.#reorderToken;
    this.#reorderByDisplayedPrice(sortBy, token);
  }

  /**
   * @param {string} sortBy
   * @param {number} token
   */
  async #reorderByDisplayedPrice(sortBy, token) {
    const { grid } = this.refs;
    if (!grid) return;

    const pageSize = Number(grid.dataset.pageSize) || 24;
    const lastPage = Number(this.dataset.shopifyLastPage) || 1;
    const descending = sortBy === 'price-descending';

    try {
      /** @type {HTMLElement[]} */
      const cards = [...grid.querySelectorAll(':scope > [ref="cards[]"]')].filter(
        (card) => card instanceof HTMLElement
      );

      if (lastPage > 1) {
        const pageNumbers = Array.from({ length: lastPage }, (_, index) => index + 1).filter((page) => {
          const visiblePage = Number(cards[0]?.dataset.page || '1');
          return page !== visiblePage;
        });

        const fetched = await Promise.all(pageNumbers.map((page) => this.#fetchGridCards(page)));
        if (token !== this.#reorderToken || !this.isConnected) return;

        for (const pageCards of fetched) cards.push(...pageCards);
      }

      const sorted = cards
        .map((card, index) => ({ card, index }))
        .sort((a, b) => {
          const aPrice = a.card.dataset.sortPrice;
          const bPrice = b.card.dataset.sortPrice;
          const aMissing = aPrice == null || aPrice === '';
          const bMissing = bPrice == null || bPrice === '';

          if (aMissing || bMissing) {
            if (aMissing && bMissing) return a.index - b.index;
            return aMissing ? 1 : -1;
          }

          const diff = Number(aPrice) - Number(bPrice);
          if (diff !== 0) return descending ? -diff : diff;
          return a.index - b.index;
        })
        .map(({ card }) => card);

      const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
      const requestedPage = Number(new URL(window.location.href).searchParams.get('page') || '1');
      const visiblePage = Math.min(Math.max(requestedPage, 1), totalPages);

      sorted.forEach((card, index) => {
        card.dataset.page = String(Math.floor(index / pageSize) + 1);
      });

      grid.setAttribute('last-page', String(totalPages));

      const start = (visiblePage - 1) * pageSize;
      this.#suppressUpdate = true;
      grid.replaceChildren(...sorted.slice(start, start + pageSize));
      this.updatedCallback();
      this.#suppressUpdate = false;

      this.pages.clear();
      for (let page = 1; page <= totalPages; page++) {
        if (page === visiblePage) continue;

        const slice = sorted.slice((page - 1) * pageSize, page * pageSize);
        const html = `<div ref="grid">${slice.map((card) => card.outerHTML).join('')}</div>`;
        this.pages.set(page, html);
      }

      if (token !== this.#reorderToken || !this.isConnected) return;
      delete this.dataset.priceSortPending;
      this.resumePaging();
    } catch (error) {
      console.error(error);
      if (token !== this.#reorderToken || !this.isConnected) return;

      grid.setAttribute('last-page', this.dataset.shopifyLastPage || '1');
      delete this.dataset.reorderPrices;
      delete this.dataset.priceSortPending;
      this.resumePaging();
    }
  }

  /**
   * @param {number} page
   * @returns {Promise<HTMLElement[]>}
   */
  async #fetchGridCards(page) {
    const url = new URL(window.location.href);
    url.searchParams.set('page', String(page));
    url.hash = '';

    const html = await sectionRenderer.getSectionHTML(this.sectionId, false, url);
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const grid = parsed.querySelector('[ref="grid"]');
    if (!grid) return [];

    return [...grid.querySelectorAll(':scope > [ref="cards[]"]')].filter((card) => card instanceof HTMLElement);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
  }

  /**
   * Updates the layout.
   *
   * @param {Event} event
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Sets the layout.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
